import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import ReactTooltip from "react-tooltip";
import Swal from "sweetalert2";
import qs from "qs";
import Breadcrumbs from "../../common/breadcrumbs";
import Icon from "../../icons";
import SearchBox from "../../common/searchBox";
import Table from "../../common/table";
import { getCurrentTimezoneDate } from "../../../util/columnsOrdering";
import { URL } from "../../../constants/URL";
import { useDeletePromotion, useGetAllPromotions } from "../../../queryHooks";
import CS from "../../../services/commonService";
import ApiLoader from "../../../loaders/ApiLoader";
import { useStore } from "../../../zustand/store";
import { pageToLoadOnDelete } from "../../../helpers";
import { modules } from "../../../constants/Modules";

//	Pre-defined values
const columnKey = "promotions";

const Promotions = () => {
	const navigate = useNavigate();
	const lastPageData = useStore((state) => state.lastPageData);
	const setLastPageData = useStore((state) => state.setLastPageData);
	const [tableData, setTableData] = useState();
	const [pageNow, setPageNow] = useState(
		lastPageData?.[modules.PROMOTIONS]?.page || 1
	);
	const [itemsPerPage, setItemsPerPage] = useState(
		lastPageData?.[modules.PROMOTIONS]?.items_per_page || 10
	);
	const [payload, setPayload] = useState(
		lastPageData?.[modules.PROMOTIONS] || {
			pagination: true,
			page: 1,
			items_per_page: 10,
			sort: {
				created_at: "DESC",
			},
		}
	);

	const {
		data: promotionsData,
		isLoading: promoLoading,
		error: promoApiError,
		refetch: promoRefetch,
	} = useGetAllPromotions(qs.stringify(payload));

	useEffect(() => {
		if (promotionsData?.status === "success") {
			let respData = promotionsData?.data?.data;
			respData.page = pageNow;
			respData.items_per_page = itemsPerPage;
			setTableData(respData);
		}
	}, [promotionsData]);

	useEffect(() => {
		if (promoApiError) {
			CS.showErrorMessage(
				`${
					promoApiError?.response?.data?.message ||
					promoApiError?.data?.message ||
					promoApiError?.message
				}`
			);
		}
	}, [promoApiError]);

	const {
		mutateAsync: deletePromoAsync,
		data: deleteApiData,
		error: deleteApiError,
		isLoading: deleteApiLoading,
	} = useDeletePromotion();

	useEffect(() => {
		if (deleteApiData) {
			let _page = pageToLoadOnDelete(
				tableData?.total_items,
				1,
				pageNow,
				itemsPerPage
			);
			if (_page === pageNow) promoRefetch();
			setPageNow(_page);
			setPayload({ ...payload, page: _page });
			CS.showSuccessMessage(
				`${deleteApiData?.data?.message || deleteApiData?.message}`
			);
		}
	}, [deleteApiData]);

	useEffect(() => {
		if (deleteApiError) {
			CS.showErrorMessage(
				`${
					deleteApiError?.response?.data?.message ||
					deleteApiError?.data?.message ||
					deleteApiError?.message
				}`
			);
		}
	}, [deleteApiError]);

	const columns = [
		{
			label: "Id",
			type: "string",
			path: "id",
			alias: "id",
		},
		{
			label: "Title",
			type: "string",
			path: "title",
			alias: "title",
			sort: true,
		},
		{
			label: "Created At",
			type: "date",
			sort: true,
			path: "created_at",
			alias: "created_at",
			filterDate: true,
			content: (item) => getCurrentTimezoneDate(item.created_at),
		},
		{
			label: "Action",
			type: "boolean",
			content: (item) => (
				<>
					<Icon
						type="show"
						data-tip
						data-for="show"
						onClick={() => {
							setLastPageData({ [modules.PROMOTIONS]: payload });
							navigate(`${URL.promotions}/${item.id}`);
						}}
					/>
					<ReactTooltip id="show" place="top" effect="solid">
						Show
					</ReactTooltip>
					<Icon
						type="delete"
						data-tip
						data-for="delete"
						onClick={() =>
							Swal.fire({
								// title: "Are you sure, you want to delete this user?",
								text: "Are you sure, you want to delete this promotion?",
								icon: "warning",
								showCancelButton: true,
								confirmButtonColor: "#3085d6",
								cancelButtonColor: "#d33",
								confirmButtonText: "Yes, delete it!",
							}).then((result) => {
								if (result.isConfirmed) {
									deletePromoAsync(item?.id);
								}
							})
						}
					/>
					<ReactTooltip id="delete" place="top" effect="solid">
						Delete
					</ReactTooltip>
				</>
			),
		},
	];

	return (
		<>
			{deleteApiLoading && <ApiLoader />}
			<Breadcrumbs title="Promotions" data={[{ label: "Promotions" }]} />
			<div className="dataTables_wrapper">
				<div className="content-area position-relative">
					<div className="grid">
						<div className="grid-content">
							<div className="user-listing-filterOptions mb-2 mt-3 d-block">
								<div className="row mb-2">
									<div className="col-sm-8 mb-2 position-static">
										<div className="left-buttons d-flex">
											<SearchBox
												columnKey={columnKey}
												searchValue={
													lastPageData?.[modules.PROMOTIONS]?.search?.title
												}
												search={(value) => {
													setPageNow(1);
													setPayload({
														...payload,
														search: {
															title:
																!value && value.trim() === ""
																	? undefined
																	: value.trim(),
															created_by:
																!value && value.trim() === ""
																	? undefined
																	: value.trim(),
														},
														page: 1,
													});
												}}
											/>
										</div>
									</div>
									<div className="col-sm-4">
										<div className="right-buttons d-flex justify-content-end">
											<button
												className="btn btn-default minW-md-0 btn-bg-white"
												onClick={() => {
													navigate(`${URL.addPromotions}`);
												}}
											>
												Create Promotion
											</button>
										</div>
									</div>
								</div>
							</div>
							<Table
								columnKey={columnKey}
								columns={columns}
								tableData={tableData}
								payload={payload}
								sortCol={(obj) => {
									setPageNow(1);
									setPayload({
										...payload,
										sort: {
											[obj.alias]:
												!obj.sort && obj.sort === "" ? undefined : obj.sort,
										},
										page: 1,
									});
								}}
								setCurrentPage={(page) => {
									setPageNow(page);
									setPayload({
										...payload,
										page,
									});
								}}
								setCurrentLimit={(items_per_page) => {
									setPageNow(1);
									setItemsPerPage(items_per_page);
									setPayload({
										...payload,
										items_per_page,
										page: 1,
									});
								}}
								loading={promoLoading}
								filterValue={async (obj) => {
									if (obj.filter === null) {
										const resp = await CS.filterData(payload.filter, obj);
										setPageNow(1);
										setPayload({
											...payload,
											filter: resp,
											page: 1,
										});
									} else {
										setPageNow(1);
										setPayload({
											...payload,
											filter: {
												...payload.filter,
												[obj.alias]: obj.filter,
											},
											page: 1,
										});
									}
								}}
								filterDate={(dates) => {
									setPageNow(1);
									setPayload({
										...payload,
										filter_date: dates && {
											...payload?.filter_date,
											[dates.alias]: String(dates.filter),
										},
										page: 1,
									});
								}}
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default Promotions;
